import { HttpAuthRepository } from '@/modules/auth/infrastructure/repositories/HttpAuthRepository';

const IFRAME_WAIT_MS = 3000;
const HTTP_WAIT_MS = 8000;
const CALLBACK_PATH = '/auth/callback';

export function waitForSilentRequest<T>(request: Promise<T>): Promise<T> {
  let timer: ReturnType<typeof setTimeout>;
  return Promise.race([
    request,
    new Promise<never>((_, reject) => {
      timer = setTimeout(() => reject(new Error('Silent OAuth request timed out')), HTTP_WAIT_MS);
    }),
  ]).finally(() => clearTimeout(timer));
}

function awaitCallback(redirectUrl: string, state: string): Promise<string> {
  return new Promise((resolve, reject) => {
    const iframe = document.createElement('iframe');
    iframe.hidden = true;
    iframe.setAttribute('aria-hidden', 'true');
    const url = new URL(redirectUrl, window.location.href);
    url.searchParams.set('prompt', 'none');
    let settled = false;

    const finish = (code?: string) => {
      if (settled) return;
      settled = true;
      clearTimeout(timeout);
      clearInterval(poll);
      iframe.remove();
      if (code) resolve(code);
      else reject(new Error('Silent OAuth authorization unavailable'));
    };

    const inspect = () => {
      try {
        const location = iframe.contentWindow?.location;
        if (!location || location.origin !== window.location.origin || location.pathname !== CALLBACK_PATH) return;
        const query = new URL(location.href).searchParams;
        if (query.get('state') !== state || query.has('error')) {
          finish();
          return;
        }
        finish(query.get('code') || undefined);
      } catch {
        // The authorization endpoint is cross-origin until it redirects back to the callback.
      }
    };

    iframe.addEventListener('load', inspect);
    iframe.addEventListener('error', () => finish());
    iframe.src = url.href;
    const timeout = setTimeout(() => finish(), IFRAME_WAIT_MS);
    const poll = setInterval(inspect, 50);
    document.body.appendChild(iframe);
  });
}

export async function checkSilentOAuthLogin(repository: HttpAuthRepository = new HttpAuthRepository()) {
  const redirect = await waitForSilentRequest(repository.getOAuthRedirectUrl('silent'));
  if (!redirect.state || new URL(redirect.redirect_url, window.location.href).searchParams.get('state') !== redirect.state) {
    throw new Error('Silent OAuth state mismatch');
  }
  const code = await awaitCallback(redirect.redirect_url, redirect.state);
  return await waitForSilentRequest(repository.oauthCallback(code, redirect.state));
}
